import type { FieldDbSchema } from './schema.js';
import type { IDBPDatabase } from 'idb';
type PhotoRow = FieldDbSchema['photos']['value'];

export function hydratePhoto(row: PhotoRow): PhotoRow {
  const { blobBytes, ...rest } = row;
  return blobBytes === undefined
    ? rest
    : {
        ...rest,
        blob: new Blob([blobBytes], { type: row.mimeType }),
        blobStorage: 'bytes',
      };
}

/** Retry only WebKit's Blob preparation failure, in a fresh write transaction. */
export async function writePhoto(db: IDBPDatabase<FieldDbSchema>, row: PhotoRow) {
  const { blobBytes: _oldBytes, ...clean } = row;
  const writeBytes = async () => {
    const { blob, ...metadata } = clean;
    const bytes = await blob!.arrayBuffer();
    if (!bytes.byteLength || bytes.byteLength !== blob!.size)
      throw new Error('Could not read the photo bytes. Choose the photo again.');
    return db.put('photos', { ...metadata, blobStorage: 'bytes', blobBytes: bytes });
  };
  if (clean.blob && clean.blobStorage === 'bytes') return writeBytes();
  try {
    return await db.put('photos', clean);
  } catch (error) {
    if (
      !clean.blob ||
      !(error instanceof Error) ||
      !/Error preparing Blob\/File data to be stored in object store/i.test(error.message)
    )
      throw error;
    return writeBytes();
  }
}

export async function readPhoto(db: IDBPDatabase<FieldDbSchema>, id: string) {
  const row = await db.get('photos', id);
  return row ? hydratePhoto(row) : undefined;
}
export async function readPhotos(
  db: IDBPDatabase<FieldDbSchema>,
  index: 'by-job' | 'by-status',
  key: string,
) {
  return (await db.getAllFromIndex('photos', index, key)).map(hydratePhoto);
}
